import { useFlag } from '@unleash/proxy-client-react';
import { useState } from 'react';
import { StateValue } from 'xstate';
import { ClaimContext } from '../../parcel-finder/claims/types';

import machine from './stateMachine';
import { ClaimShopSettings, useClaimsShopSettings } from '../../parcel-finder/ClaimsShopSettingsContext';

/**
 * We manually maintain history of states because we don't want to have BACK transitions in the state machine:
 *  - having BACK transition for each state is too verbose and error-prone in case of conditional transitions
 *  - state.history can't be used because it remembers only the last step and not the whole history
 *  - history states do not fit because it's logic depends on child-parent state configuration and transitions
 *   (see. https://stately.ai/docs/history-states)
 */

export type TransitionOptions = {
  replace?: boolean; // Whether to replace the current state in the history
  context?: Partial<ClaimContext>; // Additional context (claim) to pass to the state machine
};

export function useSubmitClaimWorkflow(claim: ClaimContext) {
  const initialStateValue = claim.draftId ? 'draft-loading' : machine.initialState.value;
  const [stateValueHistory, setStateValueHistory] = useState([initialStateValue]);
  const stateValue = stateValueHistory[stateValueHistory.length - 1];

  const pushHistory = (stateValue: StateValue) => setStateValueHistory([...stateValueHistory, stateValue]);

  const replaceHistory = (stateValue: StateValue) => {
    if (stateValueHistory.length < 1) {
      return;
    }

    setStateValueHistory([...stateValueHistory.slice(0, -1), stateValue]);
  };

  const transition = (event: string, options: TransitionOptions = {}) => {
    const { replace, context } = options;
    const nextState = machine.transition(stateValue, event, {
      ...claim,
      ...context,
    });

    if (replace) {
      replaceHistory(nextState.value);
    } else {
      pushHistory(nextState.value);
    }
  };

  const next = (options?: TransitionOptions) => transition('NEXT', options);

  const back = () => {
    if (stateValueHistory.length <= 1) {
      return;
    }

    setStateValueHistory(stateValueHistory.slice(0, -1));
  };

  const canGoBack = stateValueHistory.length > 1;

  const [workflowState, workflowStepStateValue] =
    typeof stateValue === 'object' ? Object.entries(stateValue)[0] : [null, stateValue];

  const canonicalWorkflowStep = workflowState ? `${workflowState}.${workflowStepStateValue}` : workflowStepStateValue;

  // Since we know workflowStep is always a string in our application logic,
  // we can safely cast it to string
  const workflowStep = workflowStepStateValue as string;

  return {
    workflowStep,
    canonicalWorkflowStep,
    transition,
    next,
    back: canGoBack ? back : null,
    workflowState,
  };
}
